import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useGetAgendasQuery } from 'store/services/flow/agenda';
import { useGetAllViewsQuery } from 'store/services/flow/views';
import { useGetModulesQuery } from 'store/services/core/modules';
import { useGetTemplatesQuery } from 'store/services/helios/templates';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MenuItem, styled } from '@mui/material';
import { Autocomplete, type Option } from 'components/mui/common/Autocomplete';
import { Select } from 'components/mui/common/Select';
import { ColorInput } from 'components/common/Inputs';
import type { TemplateType, ModalsType } from 'types';
import type { AgendaViewItem as AgendaViewItemType } from 'types/dashboard';
import { VIEW_TYPE } from 'types/enums';
import { TextField } from 'components/mui/common/TextField';
import { ActionModal } from 'components/mui/common/ActionModal';
import { useDashboardAccess } from 'contexts/DashboardAccessContext';

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

interface Props extends ModalsType {
  onConfirm: () => void;
  setAgendaViewData: Dispatch<SetStateAction<AgendaViewItemType>>;
  agendaData?: AgendaViewItemType;
}

type AgendaFormValues = {
  id: string;
  moduleId: string;
  agendaId: string | Option;
  viewId: string;
  borderColor?: string;
  name?: string;
};

export const AgendaViewModal = ({ open, onClose, onConfirm, setAgendaViewData, agendaData }: Props) => {
  const { t } = useTranslation();
  const { isUserDashboard, accessibleModuleIds, accessibleAgendaIds } = useDashboardAccess();

  const { control, handleSubmit, setValue, watch, reset } = useForm<AgendaFormValues>({
    defaultValues: {
      id: '',
      moduleId: '',
      agendaId: '',
      viewId: '',
      borderColor: '',
      name: '',
    },
  });

  const [watchModuleId, watchAgendaId, watchViewId] = watch(['moduleId', 'agendaId', 'viewId']);

  useEffect(() => {
    if (agendaData?.agendaView) {
      reset({
        id: agendaData.agendaView.id ?? '',
        moduleId: agendaData.agendaView.moduleId ?? '',
        agendaId: agendaData.agendaView.agendaId ?? '',
        viewId: agendaData.agendaView.viewId ?? '',
        borderColor: agendaData.borderColor ?? '',
        name: agendaData?.name ?? '',
      });
    }
  }, [agendaData, reset]);

  const { data: modulesData, isSuccess: modulesLoaded } = useGetModulesQuery({
    page: 1,
    pageSize: 1000,
    search: '',
    orderBy: 'id',
    orderDesc: false,
  });

  useEffect(() => {
    if (watchModuleId !== agendaData?.agendaView?.moduleId) {
      setValue('agendaId', '');
      setValue('viewId', '');
    }
  }, [watchModuleId, setValue, agendaData]);

  const { data: agendasData, isSuccess: agendasLoaded } = useGetAgendasQuery({
    page: 1,
    pageSize: 1000,
    search: '',
    orderBy: 'id',
    orderDesc: false,
    ModuleIds: watchModuleId ? [watchModuleId] : undefined,
    Name: '',
    AgendaId: '',
    CreateBy: '',
    Created: '',
    Status: '',
  });

  const { data: templates } = useGetTemplatesQuery(
    {
      page: 1,
      pageSize: 1000,
      orderBy: 'id',
      orderDesc: false,
      search: '',
      status: 'Active',
      moduleId: watchModuleId || undefined,
      templateType: 'Overview' as TemplateType,
    },
    { skip: !watchModuleId, refetchOnMountOrArgChange: true }
  );

  const moduleOptions = useMemo(
    () => (modulesData?.items ?? []).filter((module) => !isUserDashboard || accessibleModuleIds.has(module.id)),
    [accessibleModuleIds, isUserDashboard, modulesData?.items]
  );

  const agendasOptions = useMemo(() => {
    const agendas = (agendasData?.items ?? []).filter((agenda) => !isUserDashboard || accessibleAgendaIds.has(agenda.id.toLowerCase()));
    const templateItems = (templates?.items ?? []).filter((template) => !isUserDashboard || accessibleAgendaIds.has(template.id.toLowerCase()));

    return [
      agendas.map((agenda) => ({ label: agenda.name, value: agenda.id, isTemplate: false })),
      templateItems.map((template) => ({ label: template.name, value: template.id, isTemplate: true })),
    ].flatMap((items) => items);
  }, [accessibleAgendaIds, agendasData?.items, isUserDashboard, templates?.items]);

  const getOptionValue = (option: string | Option | undefined): string => {
    if (!option) return '';
    if (typeof option === 'object' && 'value' in option) {
      return option.value;
    }
    return option as string;
  };

  const agendaId = getOptionValue(watchAgendaId);

  const isTemplateSelected = useMemo(() => {
    const selectedAgendaOption = agendasOptions.find((o) => o.value === agendaId);

    return Boolean(selectedAgendaOption?.isTemplate);
  }, [agendasOptions, agendaId]);

  const selectedTemplate = useMemo(() => {
    if (!isTemplateSelected) return null;
    return templates?.items?.find((template) => template.id === agendaId);
  }, [isTemplateSelected, templates?.items, agendaId]);

  const { data: viewsData } = useGetAllViewsQuery(
    {
      parentId: (typeof watchAgendaId === 'object' ? watchAgendaId?.value : watchAgendaId) || '',
      viewParentType: 'Agenda',
      ExcludeHiddenViews: true,
    },
    {
      skip: !watchAgendaId,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (viewsData?.views && agendaData?.agendaView?.viewId) {
      setValue('viewId', agendaData.agendaView.viewId);
    }
  }, [viewsData, agendaData?.agendaView?.viewId, setValue]);

  const selectedView = viewsData?.views?.find((view) => view.viewId === watchViewId);

  const onSubmit = useCallback(
    async (data: AgendaFormValues) => {
      const name = data.name ? data.name : selectedTemplate ? selectedTemplate.name : selectedView?.viewName || '';

      const payload = {
        id: agendaData?.id,
        order: agendaData?.order || 0,
        borderColor: data.borderColor || '',
        name,
        type: 'AgendaView' as const,
        isNew: false,
        agendaView: {
          id: !data.id || data.id === '' ? null : data.id,
          moduleId: data.moduleId,
          agendaId: data.agendaId && typeof data.agendaId === 'object' && 'value' in data.agendaId ? data.agendaId.value : data.agendaId,
          viewId: data.viewId,
          ...(selectedTemplate?.datasourceId && {
            heliosDataSource: selectedTemplate.datasourceId,
          }),
        },
      };

      setAgendaViewData(payload as AgendaViewItemType);

      onConfirm();
    },
    [selectedTemplate, selectedView?.viewName, agendaData?.id, agendaData?.order, setAgendaViewData, onConfirm]
  );

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      onCancel={onClose}
      onSubmit={handleSubmit(onSubmit)}
      title={t('core.settings.dashboard.agendaViewSettings')}
      submitText={t('common.confirm')}>
      <Content>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField label={t('common.title')} placeholder={selectedView?.viewName || t('common.noRequiredField')} {...field} fullWidth />
          )}
        />
        <Controller
          name="moduleId"
          rules={{ required: t('common.required') }}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              fullWidth
              error={Boolean(error)}
              label={t('core.settings.dashboard.modules')}
              displayEmpty
              helperText={error?.message}
              onChange={(e) => {
                field.onChange(e);
                setValue('agendaId', '');
                setValue('viewId', '');
                setValue('name', '');
              }}>
              {moduleOptions.map((module) => (
                <MenuItem key={module.id} value={module.id}>
                  {module.name}
                </MenuItem>
              ))}
            </Select>
          )}
        />
        <Controller
          control={control}
          name="agendaId"
          rules={{
            required: {
              value: true,
              message: t('common.required'),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              data-testid="records-agenda-field"
              placeholder={t('common.select')}
              label={t('sidebar.flow.agendas')}
              disabled={!modulesLoaded || watchModuleId === ''}
              options={agendasOptions}
              {...field}
              onChange={(e) => {
                field.onChange(e);
                setValue('viewId', '');
                setValue('name', '');
              }}
              error={Boolean(error)}
              helperText={error?.message}
              background="white"
            />
          )}
        />
        {!isTemplateSelected && (
          <Controller
            name="viewId"
            rules={{ required: t('common.required') }}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                fullWidth
                error={Boolean(error)}
                helperText={error?.message}
                label={t('core.settings.dashboard.views')}
                displayEmpty
                disabled={!agendasLoaded || !watchAgendaId}>
                {(viewsData?.views ?? [])
                  .filter((view) => view.viewType === VIEW_TYPE.STANDARD)
                  .map((view) => (
                    <MenuItem key={view.viewId} value={view.viewId}>
                      {view.viewName}
                    </MenuItem>
                  ))}
              </Select>
            )}
          />
        )}
        <Controller
          control={control}
          name="borderColor"
          render={({ field }) => <ColorInput {...field} value={field?.value} label={t('core.settings.dashboard.color')} />}
        />
      </Content>
    </ActionModal>
  );
};
